/**
 * FERP Portal - Step Renderer
 *
 * Renders wizard steps for multi-step forms.
 * Handles step navigation, validation, and conditional visibility.
 *
 * CONSTRAINTS:
 * - All steps must validate before proceeding
 * - Step visibility is condition-driven
 * - Life-critical forms may require step-level approval
 */

import { memo, useMemo, useCallback } from 'react';
import {
  Stepper,
  Box,
  Group,
  Button,
  Paper,
  Title,
  Text,
  Stack,
  Collapse,
  ActionIcon,
  Badge,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconLock,
} from '@tabler/icons-react';
import { FieldRenderer } from './FieldRenderer';
import type {
  StepDefinition,
  StepRendererProps,
  RendererContext,
  FieldDefinition,
} from './types';

// ============================================================================
// STEP RENDERER
// ============================================================================

interface StepContentProps {
  step: StepDefinition;
  context: RendererContext;
  isActive: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

/**
 * Renders the content of a single step
 */
const StepContent = memo(function StepContent({
  step,
  context,
  isActive,
  isCollapsed,
  onToggleCollapse,
}: StepContentProps) {
  const { state, setValue, setTouched } = context;

  /**
   * Handle field change
   */
  const handleFieldChange = useCallback(
    (fieldName: string) => (value: unknown) => {
      setValue(fieldName, value);
    },
    [setValue]
  );

  /**
   * Handle field blur
   */
  const handleFieldBlur = useCallback(
    (fieldName: string) => () => {
      setTouched(fieldName, true);
    },
    [setTouched]
  );

  /**
   * Get visible fields (respecting visibility conditions)
   */
  const visibleFields = useMemo(() => {
    return step.fields.filter((field) => {
      if (!field.visibility) return true;

      const { showWhen, hideWhen } = field.visibility;

      if (hideWhen && context.evaluateCondition(hideWhen)) {
        return false;
      }

      if (showWhen && !context.evaluateCondition(showWhen)) {
        return false;
      }

      return true;
    });
  }, [step.fields, context]);

  return (
    <Paper
      p="lg"
      withBorder={!isActive}
      shadow={isActive ? 'sm' : undefined}
      style={{
        backgroundColor: isActive ? undefined : 'var(--mantine-color-gray-0)',
        opacity: isActive ? 1 : 0.8,
      }}
    >
      {/* Step Header */}
      <Group justify="space-between" mb={isCollapsed ? 0 : 'md'}>
        <Group gap="sm">
          {step.icon && (
            <ThemeIcon size="lg" variant="light">
              <span>{step.icon}</span>
            </ThemeIcon>
          )}
          <Box>
            <Title order={4}>{step.title}</Title>
            {step.description && !isCollapsed && (
              <Text size="sm" c="dimmed">
                {step.description}
              </Text>
            )}
          </Box>
        </Group>

        {step.collapsible && onToggleCollapse && (
          <ActionIcon variant="subtle" onClick={onToggleCollapse}>
            {isCollapsed ? <IconChevronDown size={18} /> : <IconChevronUp size={18} />}
          </ActionIcon>
        )}
      </Group>

      {/* Step Fields */}
      <Collapse in={!isCollapsed}>
        <Stack gap="md">
          {visibleFields.map((field) => (
            <FieldRenderer
              key={field.name}
              field={field}
              value={state.values[field.name]}
              error={state.touched[field.name] ? state.errors[field.name] : undefined}
              touched={state.touched[field.name]}
              disabled={!isActive}
              onChange={handleFieldChange(field.name)}
              onBlur={handleFieldBlur(field.name)}
              context={context}
            />
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
});

// ============================================================================
// WIZARD STEPPER
// ============================================================================

interface WizardStepperProps {
  steps: StepDefinition[];
  currentStep: number;
  completedSteps: Set<number>;
  errorSteps: Set<number>;
  context: RendererContext;
  onStepClick?: (step: number) => void;
  allowStepNavigation?: boolean;
}

/**
 * Stepper component for wizard navigation
 */
export const WizardStepper = memo(function WizardStepper({
  steps,
  currentStep,
  completedSteps,
  errorSteps,
  context,
  onStepClick,
  allowStepNavigation = true,
}: WizardStepperProps) {
  /**
   * Get visible steps (respecting skip conditions)
   */
  const visibleSteps = useMemo(() => {
    return steps.filter((step) => {
      if (step.skipWhen && context.evaluateCondition(step.skipWhen)) {
        return false;
      }
      if (!step.visibility) return true;

      const { showWhen, hideWhen } = step.visibility;

      if (hideWhen && context.evaluateCondition(hideWhen)) {
        return false;
      }

      if (showWhen && !context.evaluateCondition(showWhen)) {
        return false;
      }

      return true;
    });
  }, [steps, context]);

  /**
   * Get step icon based on state
   */
  const getStepIcon = (index: number, step: StepDefinition) => {
    if (errorSteps.has(index)) {
      return step.errorIcon || <IconAlertCircle size={18} />;
    }
    if (completedSteps.has(index)) {
      return step.completedIcon || <IconCheck size={18} />;
    }
    if (step.icon) {
      return <span>{step.icon}</span>;
    }
    return null;
  };

  /**
   * Handle step click
   */
  const handleStepClick = (stepIndex: number) => {
    if (!allowStepNavigation) return;
    if (stepIndex > currentStep && !completedSteps.has(stepIndex - 1)) return;
    onStepClick?.(stepIndex);
  };

  return (
    <Stepper
      active={currentStep}
      onStepClick={allowStepNavigation ? handleStepClick : undefined}
      completedIcon={<IconCheck size={18} />}
      color={errorSteps.has(currentStep) ? 'red' : 'blue'}
    >
      {visibleSteps.map((step, index) => (
        <Stepper.Step
          key={step.id}
          label={step.title}
          description={step.description}
          icon={getStepIcon(index, step)}
          color={errorSteps.has(index) ? 'red' : completedSteps.has(index) ? 'green' : 'blue'}
          loading={index === currentStep && context.state.isSubmitting}
          allowStepSelect={allowStepNavigation && (index <= currentStep || completedSteps.has(index - 1))}
        >
          <StepContent step={step} context={context} isActive={index === currentStep} />
        </Stepper.Step>
      ))}
    </Stepper>
  );
});

// ============================================================================
// STEP NAVIGATION
// ============================================================================

interface StepNavigationProps {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  isStepValid: boolean;
  isSubmitting: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
  previousLabel?: string;
  nextLabel?: string;
  submitLabel?: string;
}

/**
 * Navigation buttons for wizard steps
 */
export const StepNavigation = memo(function StepNavigation({
  currentStep,
  totalSteps,
  isFirstStep,
  isLastStep,
  isStepValid,
  isSubmitting,
  onPrevious,
  onNext,
  onSubmit,
  previousLabel = 'Previous',
  nextLabel = 'Next',
  submitLabel = 'Submit',
}: StepNavigationProps) {
  return (
    <Group justify="space-between" mt="xl">
      <Group>
        <Button
          variant="subtle"
          onClick={onPrevious}
          disabled={isFirstStep || isSubmitting}
        >
          {previousLabel}
        </Button>
      </Group>

      <Group>
        <Badge variant="light" size="lg">
          Step {currentStep + 1} of {totalSteps}
        </Badge>
      </Group>

      <Group>
        {isLastStep ? (
          <Button
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={!isStepValid}
          >
            {submitLabel}
          </Button>
        ) : (
          <Button
            onClick={onNext}
            disabled={!isStepValid || isSubmitting}
          >
            {nextLabel}
          </Button>
        )}
      </Group>
    </Group>
  );
});

// ============================================================================
// STEP SUMMARY
// ============================================================================

interface StepSummaryProps {
  steps: StepDefinition[];
  values: Record<string, unknown>;
  context: RendererContext;
}

/**
 * Summary view of all steps and their values
 */
export const StepSummary = memo(function StepSummary({
  steps,
  values,
  context,
}: StepSummaryProps) {
  /**
   * Format field value for display
   */
  const formatValue = (field: FieldDefinition, value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  /**
   * Get visible steps
   */
  const visibleSteps = useMemo(() => {
    return steps.filter((step) => {
      if (step.skipWhen && context.evaluateCondition(step.skipWhen)) {
        return false;
      }
      return true;
    });
  }, [steps, context]);

  return (
    <Stack gap="lg">
      {visibleSteps.map((step) => (
        <Paper key={step.id} p="md" withBorder>
          <Group gap="sm" mb="sm">
            {step.icon && (
              <ThemeIcon size="sm" variant="light">
                <span>{step.icon}</span>
              </ThemeIcon>
            )}
            <Title order={5}>{step.title}</Title>
          </Group>

          <Stack gap="xs">
            {step.fields.map((field) => {
              // Skip hidden fields
              if (field.visibility) {
                const { hideWhen } = field.visibility;
                if (hideWhen && context.evaluateCondition(hideWhen)) {
                  return null;
                }
              }

              return (
                <Group key={field.name} gap="xs">
                  <Text size="sm" c="dimmed" style={{ minWidth: 150 }}>
                    {field.label}:
                  </Text>
                  <Text size="sm" fw={500}>
                    {formatValue(field, values[field.name])}
                  </Text>
                </Group>
              );
            })}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
});

// ============================================================================
// ACCORDION STEPS
// ============================================================================

interface AccordionStepsProps {
  steps: StepDefinition[];
  context: RendererContext;
  completedSteps: Set<number>;
  errorSteps: Set<number>;
  expandedSteps: Set<number>;
  onToggleStep: (index: number) => void;
}

/**
 * Accordion-style step rendering for non-linear forms
 */
export const AccordionSteps = memo(function AccordionSteps({
  steps,
  context,
  completedSteps,
  errorSteps,
  expandedSteps,
  onToggleStep,
}: AccordionStepsProps) {
  /**
   * Get visible steps
   */
  const visibleSteps = useMemo(() => {
    return steps.filter((step) => {
      if (step.skipWhen && context.evaluateCondition(step.skipWhen)) {
        return false;
      }
      if (!step.visibility) return true;

      const { showWhen, hideWhen } = step.visibility;

      if (hideWhen && context.evaluateCondition(hideWhen)) {
        return false;
      }

      if (showWhen && !context.evaluateCondition(showWhen)) {
        return false;
      }

      return true;
    });
  }, [steps, context]);

  return (
    <Stack gap="md">
      {visibleSteps.map((step, index) => {
        const isExpanded = expandedSteps.has(index);
        const isCompleted = completedSteps.has(index);
        const hasError = errorSteps.has(index);

        return (
          <Paper
            key={step.id}
            withBorder
            style={{
              borderColor: hasError
                ? 'var(--mantine-color-red-5)'
                : isCompleted
                ? 'var(--mantine-color-green-5)'
                : undefined,
            }}
          >
            {/* Step Header */}
            <Group
              p="md"
              justify="space-between"
              style={{
                cursor: step.collapsible !== false ? 'pointer' : undefined,
                backgroundColor: isExpanded ? undefined : 'var(--mantine-color-gray-0)',
              }}
              onClick={() => step.collapsible !== false && onToggleStep(index)}
            >
              <Group gap="sm">
                <ThemeIcon
                  size="md"
                  variant="light"
                  color={hasError ? 'red' : isCompleted ? 'green' : 'blue'}
                >
                  {hasError ? (
                    <IconAlertCircle size={16} />
                  ) : isCompleted ? (
                    <IconCheck size={16} />
                  ) : step.icon ? (
                    <span>{step.icon}</span>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </ThemeIcon>
                <Box>
                  <Text fw={500}>{step.title}</Text>
                  {step.description && (
                    <Text size="xs" c="dimmed">
                      {step.description}
                    </Text>
                  )}
                </Box>
              </Group>

              <Group gap="xs">
                {isCompleted && (
                  <Badge size="sm" color="green" variant="light">
                    Complete
                  </Badge>
                )}
                {hasError && (
                  <Badge size="sm" color="red" variant="light">
                    Has Errors
                  </Badge>
                )}
                {step.collapsible !== false && (
                  <ActionIcon variant="subtle">
                    {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                  </ActionIcon>
                )}
              </Group>
            </Group>

            {/* Step Content */}
            <Collapse in={isExpanded}>
              <Box p="md" pt={0}>
                <StepContent
                  step={step}
                  context={context}
                  isActive={true}
                  isCollapsed={false}
                />
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
});

// ============================================================================
// SINGLE STEP RENDERER
// ============================================================================

/**
 * Main step renderer component
 * Can be used for individual step rendering
 */
export const StepRenderer = memo(function StepRenderer({
  step,
  stepIndex,
  isActive,
  isCompleted,
  hasError,
  context,
}: StepRendererProps) {
  /**
   * Check if step should be visible
   */
  const isVisible = useMemo(() => {
    if (step.skipWhen && context.evaluateCondition(step.skipWhen)) {
      return false;
    }
    if (!step.visibility) return true;

    const { showWhen, hideWhen } = step.visibility;

    if (hideWhen && context.evaluateCondition(hideWhen)) {
      return false;
    }

    if (showWhen && !context.evaluateCondition(showWhen)) {
      return false;
    }

    return true;
  }, [step.visibility, step.skipWhen, context]);

  if (!isVisible) {
    return null;
  }

  return (
    <Box>
      {/* Step indicator */}
      <Group gap="sm" mb="md">
        <ThemeIcon
          size="lg"
          variant={isActive ? 'filled' : 'light'}
          color={hasError ? 'red' : isCompleted ? 'green' : 'blue'}
        >
          {hasError ? (
            <IconAlertCircle size={20} />
          ) : isCompleted ? (
            <IconCheck size={20} />
          ) : (
            <span>{stepIndex + 1}</span>
          )}
        </ThemeIcon>
        <Box>
          <Title order={3}>{step.title}</Title>
          {step.description && (
            <Text c="dimmed" size="sm">
              {step.description}
            </Text>
          )}
        </Box>
      </Group>

      {/* Step content */}
      <StepContent step={step} context={context} isActive={isActive} />
    </Box>
  );
});

// ============================================================================
// EXPORTS
// ============================================================================

export default StepRenderer;
